<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="1000px"
    overlay-opacity="0.95"
    content-class="modern-leaderboard-dialog"
    z-index="3000"
    fullscreen-mobile
  >
    <v-card class="rounded-xl elevation-24">
      <!-- Header with glassmorphism effect -->
      <v-card-title class="leaderboard-header pa-0">
        <div class="header-content">
          <div class="header-left">
            <div class="trophy-container">
              <v-icon x-large color="white" class="trophy-icon">mdi-trophy-award</v-icon>
              <div class="trophy-glow"></div>
            </div>
            <div class="header-text">
              <div class="header-title">Agency Leaderboard</div>
              <div class="header-subtitle">{{ event && event.competitionData ? event.competitionData.title : '' }}</div>
              <div class="stats-chip">
                <v-icon small class="mr-1">mdi-office-building</v-icon>
                {{ detailedPoints.length }} agencies
              </div>
            </div>
          </div>
          <v-btn
            icon
            @click="onClose"
            class="close-btn"
            x-large
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <!-- Search and filters section -->
      <div class="search-section">
        <div class="search-container">
          <v-text-field
            v-model="searchQuery"
            placeholder="Search agencies..."
            outlined
            dense
            hide-details
            class="search-field"
            prepend-inner-icon="mdi-magnify"
            clearable
          >
            <template v-slot:append>
              <v-btn
                icon
                small
                @click="toggleAdvancedSearch"
                :class="{ 'active': showAdvancedSearch }"
                class="filter-btn"
              >
                <v-icon>mdi-filter-variant</v-icon>
              </v-btn>
            </template>
          </v-text-field>
        </div>
        
        <!-- Advanced search options -->
        <v-expand-transition>
          <div v-if="showAdvancedSearch" class="advanced-search">
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="sortBy"
                  :items="sortOptions"
                  label="Sort by"
                  outlined
                  dense
                  hide-details
                ></v-select>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>
      </div>

      <!-- Main content -->
      <v-card-text class="pa-0" style="max-height: 65vh; overflow-y: auto">
        <div class="leaderboard-container">
          <!-- Podium for top 3 (only on larger screens) -->
          <div v-if="topAgencies.length >= 3" class="podium-section d-none d-md-block">
            <div class="podium-title">
              <v-icon color="primary" class="mr-2">mdi-podium-gold</v-icon>
              Top Performers
            </div>
            <div class="podium-container">
              <div class="podium-item second-place" data-aos="fade-up" data-aos-delay="100">
                <div class="podium-decoration">
                  <div class="podium-rank">2</div>
                  <v-icon color="white" size="20">mdi-medal-outline</v-icon>
                </div>
                <v-avatar size="80" class="podium-avatar">
                  <img :src="topAgencies[1].agency_midThumbnail || placeholderImage" :alt="topAgencies[1].agency_name">
                  <div class="avatar-ring second"></div>
                </v-avatar>
                <div class="podium-info">
                  <div class="podium-name">{{ truncateName(topAgencies[1].agency_name) }}</div>
                  <div class="podium-points">{{ formatPoints(topAgencies[1].total_points) }}</div>
                </div>
              </div>
              
              <div class="podium-item first-place" data-aos="fade-up">
                <div class="podium-decoration">
                  <div class="podium-rank">1</div>
                  <v-icon color="white" size="24">mdi-crown</v-icon>
                </div>
                <v-avatar size="100" class="podium-avatar">
                  <img :src="topAgencies[0].agency_midThumbnail || placeholderImage" :alt="topAgencies[0].agency_name">
                  <div class="avatar-ring first"></div>
                  <div class="crown-effect">👑</div>
                </v-avatar>
                <div class="podium-info">
                  <div class="podium-name">{{ truncateName(topAgencies[0].agency_name) }}</div>
                  <div class="podium-points">{{ formatPoints(topAgencies[0].total_points) }}</div>
                </div>
              </div>
              
              <div class="podium-item third-place" data-aos="fade-up" data-aos-delay="200">
                <div class="podium-decoration">
                  <div class="podium-rank">3</div>
                  <v-icon color="white" size="18">mdi-medal-outline</v-icon>
                </div>
                <v-avatar size="70" class="podium-avatar">
                  <img :src="topAgencies[2].agency_midThumbnail || placeholderImage" :alt="topAgencies[2].agency_name">
                  <div class="avatar-ring third"></div>
                </v-avatar>
                <div class="podium-info">
                  <div class="podium-name">{{ truncateName(topAgencies[2].agency_name) }}</div>
                  <div class="podium-points">{{ formatPoints(topAgencies[2].total_points) }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Participants list with pagination -->
          <div class="participants-section">
            <div class="section-header">
              <h3 class="section-title">
                <v-icon color="primary" class="mr-2">mdi-format-list-numbered</v-icon>
                All Participants
              </h3>
              <div class="results-info">
                Showing {{ (currentPage - 1) * itemsPerPage + 1 }}-{{ Math.min(currentPage * itemsPerPage, filteredAgencies.length) }} 
                of {{ filteredAgencies.length }} agencies
              </div>
            </div>

            <div class="participants-list">
              <div 
                v-for="(agency, index) in paginatedAgencies" 
                :key="agency.agency_email || index"
                class="participant-item"
                :class="getParticipantClasses(agency, getRealIndex(index))"
                @click="showAgencyDetails(agency)"
              >
                <div class="participant-rank">
                  <div class="rank-container">
                    <span class="rank-number">{{ getRealIndex(index) + 1 }}</span>
                    <div class="rank-change" v-if="agency.rankChange">
                      <v-icon 
                        :color="agency.rankChange > 0 ? 'success' : 'error'"
                        size="12"
                      >
                        {{ agency.rankChange > 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}
                      </v-icon>
                      <span class="change-text">{{ Math.abs(agency.rankChange) }}</span>
                    </div>
                  </div>
                  <div class="medal-container">
                    <v-icon v-if="getRealIndex(index) === 0" color="#FFD700" size="18">mdi-crown</v-icon>
                    <v-icon v-else-if="getRealIndex(index) === 1" color="#C0C0C0" size="16">mdi-medal</v-icon>
                    <v-icon v-else-if="getRealIndex(index) === 2" color="#CD7F32" size="16">mdi-medal</v-icon>
                  </div>
                </div>
                
                <div class="participant-avatar-container">
                  <v-avatar size="50" class="participant-avatar">
                    <img :src="agency.agency_midThumbnail || placeholderImage" :alt="agency.agency_name">
                  </v-avatar>
                </div>
                
                <div class="participant-info">
                  <div class="participant-name">
                    {{ agency.agency_name }}
                  </div>
                  <div class="participant-details">
                    <span class="participant-email">{{ agency.agency_email }}</span>
                  </div>
                </div>
                
                <div class="participant-stats">
                  <div class="points-container">
                    <animated-number
                      :value="agency.total_points"
                      :duration="800 + index * 100"
                      :formatValue="formatPoints"
                      easing="easeOutQuad"
                      class="points-badge"
                    />
                    <div class="points-label">points</div>
                  </div>
                  <div class="progress-section">
                    <v-progress-linear
                      :value="(agency.total_points / maxPoints) * 100"
                      height="4"
                      rounded
                      :color="getProgressColor(getRealIndex(index))"
                      class="progress-bar"
                    ></v-progress-linear>
                    <div class="progress-percentage">
                      {{ Math.round((agency.total_points / maxPoints) * 100) }}%
                    </div>
                  </div>
                </div>

                <div class="participant-actions">
                  <v-btn
                    icon
                    small
                    @click.stop="showAgencyDetails(agency)"
                    class="action-btn"
                  >
                    <v-icon size="16">mdi-account-details</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>

            <!-- Empty state -->
            <div v-if="filteredAgencies.length === 0" class="empty-state">
              <div class="empty-icon">
                <v-icon x-large color="grey">mdi-office-building-outline</v-icon>
              </div>
              <div class="empty-title">No agencies found</div>
              <div class="empty-subtitle">
                {{ searchQuery ? 'Try adjusting your search criteria' : 'Be the first to join the competition!' }}
              </div>
              <v-btn
                v-if="searchQuery"
                @click="clearSearch"
                color="primary"
                outlined
                class="mt-4"
              >
                Clear Search
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Pagination and actions -->
      <v-card-actions class="footer-actions">
        <div class="pagination-container">
          <v-pagination
            v-if="totalPages > 1"
            v-model="currentPage"
            :length="totalPages"
            :total-visible="$vuetify.breakpoint.xs ? 5 : 7"
            color="primary"
            class="custom-pagination"
          ></v-pagination>
        </div>
        
        <div class="action-buttons">
          <v-btn
            @click="exportAsPDF"
            color="success"
            outlined
            class="mr-3"
            :disabled="filteredAgencies.length === 0"
            :loading="pdfLoading"
          >
            <v-icon left>mdi-download</v-icon>
            Export PDF
          </v-btn>
          <v-btn
            @click="onClose"
            color="primary"
            large
            class="close-button"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Close
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import AnimatedNumber from "animated-number-vue";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export default {
  components: {
    AnimatedNumber
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function
    },
    detailedPoints: {
      type: Array,
      default: () => []
    },
    event: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      placeholderImage: require('@/assets/default.jpg'),
      searchQuery: '',
      showAdvancedSearch: false,
      sortBy: 'points',
      filterBy: null,
      currentPage: 1,
      itemsPerPage: 5,
      selectedAgency: null,
      pdfLoading: false,
      sortOptions: [
        { text: 'Points (High to Low)', value: 'points' },
        { text: 'Points (Low to High)', value: 'points_asc' },
        { text: 'Name (A-Z)', value: 'name' },
        { text: 'Name (Z-A)', value: 'name_desc' }
      ],
      filterOptions: [
        { text: 'Top 10', value: 'top10' },
        { text: 'Top 25', value: 'top25' },
        { text: 'Top 50', value: 'top50' },
        { text: 'Bottom 25%', value: 'bottom25' }
      ]
    };
  },
  computed: {
    sortedAgencies() {
      let agencies = [...(this.detailedPoints || [])];
      
      switch (this.sortBy) {
        case 'points':
          return agencies.sort((a, b) => b.total_points - a.total_points);
        case 'points_asc':
          return agencies.sort((a, b) => a.total_points - b.total_points);
        case 'name':
          return agencies.sort((a, b) => 
            a.agency_name.localeCompare(b.agency_name)
          );
        case 'name_desc':
          return agencies.sort((a, b) => 
            b.agency_name.localeCompare(a.agency_name)
          );
        default:
          return agencies.sort((a, b) => b.total_points - a.total_points);
      }
    },
    filteredAgencies() {
      let filtered = this.sortedAgencies;
      
      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(agency => 
          agency.agency_name.toLowerCase().includes(query) ||
          (agency.agency_email && agency.agency_email.toLowerCase().includes(query))
      )}
      
      return filtered;
    },
    paginatedAgencies() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredAgencies.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredAgencies.length / this.itemsPerPage);
    },
    topAgencies() {
      return this.sortedAgencies.slice(0, 3);
    },
    maxPoints() {
      if (this.sortedAgencies.length === 0) return 100;
      return this.sortedAgencies[0].total_points || 100;
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    filterBy() {
      this.currentPage = 1;
    },
    sortBy() {
      this.currentPage = 1;
    }
  },
  methods: {
    getProgressColor(index) {
      if (index === 0) return '#FFD700';
      if (index === 1) return '#C0C0C0';
      if (index === 2) return '#CD7F32';
      return 'linear-gradient(90deg, #3b7ca1 0%, #084468 100%)';
    },
    getParticipantClasses(agency, index) {
      return {
        'top-three': index < 3,
        'first-place-item': index === 0,
        'clickable': true
      };
    },
    getRealIndex(paginatedIndex) {
      return (this.currentPage - 1) * this.itemsPerPage + paginatedIndex;
    },
    formatPoints(value) {
      if (value >= 1000000) {
        return Math.floor(value / 100000) / 10 + 'M';
      }
      if (value >= 1000) {
        return Math.floor(value / 100) / 10 + 'K';
      }
      return value.toString();
    },
    toggleAdvancedSearch() {
      this.showAdvancedSearch = !this.showAdvancedSearch;
    },
    clearSearch() {
      this.searchQuery = '';
      this.filterBy = null;
      this.sortBy = 'points';
    },
    truncateName(name, length = 20) {
      if (!name) return '';
      return name.length > length ? name.slice(0, length) + '...' : name;
    },
    showAgencyDetails(agency) {
      this.selectedAgency = agency;
      // You can implement agency details view here agency_id
        this.$router.push({
          name: "agency-profile-details",
          params: { id: agency.agency_id }
        });
    },
    async exportAsPDF() {
      this.pdfLoading = true;

      try {
        // Create new jsPDF instance
        const doc = new jsPDF({
          orientation: "landscape",
          unit: "mm"
        });

        // Add title
        doc.setFontSize(18);
        doc.setTextColor(40);
        doc.text("Competition Standings Report", 105, 15, { align: "center" });

        // Add subtitle with date
        doc.setFontSize(12);
        doc.setTextColor(100);
        doc.text(`Generated on ${new Date().toLocaleDateString()}`, 105, 22, {
          align: "center"
        });

        // Prepare table data
        const headers = ["Rank", "Agency Name", "Email", "Points"];

        const tableData = this.filteredAgencies.map((point, index) => [
          index + 1,
          point.agency_name,
          point.agency_email,
          point.total_points
        ]);

        // Add the table using autoTable
        autoTable(doc, {
          head: [headers],
          body: tableData,
          startY: 30,
          styles: {
            fontSize: 10,
            cellPadding: 3,
            valign: "middle"
          },
          headStyles: {
            fillColor: [58, 123, 213],
            textColor: 255,
            fontStyle: "bold"
          },
          alternateRowStyles: {
            fillColor: [240, 240, 240]
          },
          columnStyles: {
            0: { cellWidth: 15, halign: "center" }, // Rank
            3: { cellWidth: 20, halign: "right" } // Points
          },
          didDrawPage: data => {
            // Footer
            doc.setFontSize(10);
            doc.setTextColor(150);
            doc.text(
              `Page ${data.pageNumber}`,
              data.settings.margin.left,
              doc.internal.pageSize.height - 10
            );
          }
        });

        // Highlight top 3 positions
        if (tableData.length >= 3) {
          autoTable(doc, {
            body: [["🏆 1st Place", "🥈 2nd Place", "🥉 3rd Place"]],
            startY: doc.lastAutoTable.finalY + 10,
            styles: {
              fontSize: 12,
              cellPadding: 5,
              halign: "center",
              fillColor: [255, 255, 255],
              textColor: [58, 123, 213],
              fontStyle: "bold"
            },
            columnStyles: {
              0: { cellWidth: 60 },
              1: { cellWidth: 60 },
              2: { cellWidth: 60 }
            }
          });
        }

        // Save the PDF
        doc.save(
          `competition-standings-${new Date().toISOString().slice(0, 10)}.pdf`
        );
      } catch (error) {
        console.error("Error generating PDF:", error);
      } finally {
        this.pdfLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.modern-leaderboard-dialog {
  border-radius: 24px !important;
  overflow: hidden;
  backdrop-filter: blur(20px);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.4) !important;
}

.leaderboard-header {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  background-size: 200% 200%;
  animation: gradientShift 10s ease infinite;
  position: relative;
  overflow: hidden;
}

.leaderboard-header::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(transparent, rgba(255,255,255,0.1), transparent 30%);
  animation: rotate 4s linear infinite;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 24px 32px;
  position: relative;
  z-index: 2;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.trophy-container {
  position: relative;
}

.trophy-icon {
  position: relative;
  z-index: 2;
  filter: drop-shadow(0 0 10px rgba(255,255,255,0.5));
}

.trophy-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: radial-gradient(circle, rgba(255,255,255,0.3), transparent);
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.header-title {
  font-size: 28px;
  font-weight: 700;
  color: white;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

.header-subtitle {
  font-size: 16px;
  color: rgba(255,255,255,0.9);
  font-weight: 400;
}

.stats-chip {
  display: inline-flex;
  align-items: center;
  background: rgba(255,255,255,0.2);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  margin-top: 8px;
  backdrop-filter: blur(10px);
}

.close-btn {
  background: rgba(255,255,255,0.1) !important;
  color: white !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
}

.close-btn:hover {
  background: rgba(255,255,255,0.2) !important;
  transform: rotate(90deg) scale(1.1);
}

.search-section {
  padding: 24px 32px 16px;
  background: linear-gradient(180deg, #f8faff 0%, #ffffff 100%);
}

.search-container {
  position: relative;
}

.search-field {
  border-radius: 16px !important;
}

.search-field ::v-deep .v-input__control {
  border-radius: 16px !important;
}

.search-field ::v-deep .v-text-field__details {
  display: none;
}

.filter-btn {
  transition: all 0.3s ease;
}

.filter-btn.active {
  background: rgba(102, 126, 234, 0.1) !important;
  color: #084468 !important;
}

.advanced-search {
  margin-top: 16px;
  padding: 16px;
  background: rgba(102, 126, 234, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.leaderboard-container {
  padding: 0 32px 24px;
}

.podium-section {
  margin-bottom: 40px;
}

.podium-title {
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
  justify-content: center;
}

.podium-container {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 24px;
  padding: 0 20px;
}

.podium-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
  border-radius: 20px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255,255,255,0.2);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.podium-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.podium-item:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0,0,0,0.15);
}

.podium-item:hover::before {
  opacity: 1;
}

.first-place {
  height: 280px;
  background: linear-gradient(135deg, #FFD700 0%, #FFA000 100%);
  order: 2;
  box-shadow: 0 15px 35px rgba(255, 215, 0, 0.3);
}

.second-place {
  height: 240px;
  background: linear-gradient(135deg, #C0C0C0 0%, #9E9E9E 100%);
  order: 1;
  box-shadow: 0 15px 35px rgba(192, 192, 192, 0.3);
}

.third-place {
  height: 220px;
  background: linear-gradient(135deg, #CD7F32 0%, #BF6000 100%);
  order: 3;
  box-shadow: 0 15px 35px rgba(205, 127, 50, 0.3);
}

.podium-decoration {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.podium-rank {
  font-size: 36px;
  font-weight: 800;
  color: white;
  text-shadow: 0 2px 8px rgba(0,0,0,0.3);
}

.podium-avatar {
  position: relative;
  margin-bottom: 16px;
  transition: transform 0.3s ease;
}

.podium-avatar:hover {
  transform: scale(1.05);
}

.avatar-ring {
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border-radius: 50%;
  border: 3px solid;
  animation: ringPulse 2s ease-in-out infinite;
}

.avatar-ring.first {
  border-color: #FFD700;
  box-shadow: 0 0 20px rgba(255, 215, 0, 0.5);
}

.avatar-ring.second {
  border-color: #C0C0C0;
  box-shadow: 0 0 20px rgba(192, 192, 192, 0.5);
}

.avatar-ring.third {
  border-color: #CD7F32;
  box-shadow: 0 0 20px rgba(205, 127, 50, 0.5);
}

.crown-effect {
  position: absolute;
  top: -20px;
  right: -15px;
  font-size: 28px;
  animation: bounce 2s ease-in-out infinite;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
}

.podium-info {
  text-align: center;
}

.podium-name {
  font-weight: 700;
  font-size: 18px;
  color: white;
  margin-bottom: 8px;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  line-height: 1.2;
}

.podium-points {
  font-size: 16px;
  color: white;
  font-weight: 600;
  background: rgba(0,0,0,0.2);
  padding: 6px 16px;
  border-radius: 20px;
  backdrop-filter: blur(10px);
}

.participants-section {
  margin-top: 32px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 22px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.results-info {
  font-size: 14px;
  color: #666;
  background: #f5f7fa;
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid #e1e8ed;
}

.participants-list {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  border: 1px solid #f0f2f5;
}

.participant-item {
  display: flex;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f8fafc;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  cursor: pointer;
}

.participant-item:last-child {
  border-bottom: none;
}

.participant-item:hover {
  background: linear-gradient(90deg, #f8faff 0%, #ffffff 100%);
  transform: translateX(8px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
}

.participant-item.first-place-item {
  background: linear-gradient(90deg, rgba(255,215,0,0.1) 0%, rgba(255,215,0,0.05) 100%);
  border-left: 4px solid #FFD700;
}

.participant-rank {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
  margin-right: 16px;
}

.rank-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rank-number {
  font-weight: 700;
  font-size: 18px;
  color: #333;
  min-width: 30px;
  text-align: center;
}

.rank-change {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 8px;
  background: rgba(0,0,0,0.05);
}

.change-text {
  font-weight: 600;
}

.medal-container {
  margin-top: 4px;
  height: 20px;
  display: flex;
  justify-content: center;
}

.participant-avatar-container {
  position: relative;
  margin-right: 20px;
}

.participant-avatar {
  border: 3px solid #f0f2f5;
  transition: all 0.3s ease;
}

.participant-item:hover .participant-avatar {
  border-color: #084468;
  transform: scale(1.05);
}

.participant-info {
  flex: 1;
  min-width: 0;
  margin-right: 20px;
}

.participant-name {
  font-weight: 600;
  font-size: 16px;
  color: #333;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.participant-details {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #666;
}

.participant-email {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.participant-stats {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 120px;
  margin-right: 16px;
}

.points-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.points-badge {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 16px;
  min-width: 60px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.points-label {
  font-size: 10px;
  color: #888;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.progress-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.progress-bar {
  border-radius: 4px !important;
}

.progress-percentage {
  font-size: 10px;
  color: #888;
  text-align: center;
  font-weight: 500;
}

.participant-actions {
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.participant-item:hover .participant-actions {
  opacity: 1;
}

.action-btn {
  background: rgba(102, 126, 234, 0.1) !important;
  color: #084468 !important;
  transition: all 0.3s ease;
}

.action-btn:hover {
  background: rgba(102, 126, 234, 0.2) !important;
  transform: scale(1.1);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;
  background: linear-gradient(135deg, #f8faff 0%, #ffffff 100%);
  border-radius: 16px;
  margin: 20px 0;
}

.empty-icon {
  margin-bottom: 24px;
  opacity: 0.6;
}

.empty-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.empty-subtitle {
  font-size: 16px;
  color: #666;
  max-width: 400px;
  line-height: 1.5;
}

.footer-actions {
  padding: 24px 32px;
  background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%);
  border-top: 1px solid #f0f2f5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.pagination-container {
  flex: 1;
  display: flex;
  justify-content: center;
}

.custom-pagination ::v-deep .v-pagination__item {
  border-radius: 12px;
  margin: 0 4px;
  transition: all 0.3s ease;
}

.custom-pagination ::v-deep .v-pagination__item--active {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%) !important;
  color: white !important;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.action-buttons {
  display: flex;
  gap: 16px;
  align-items: center;
}

.close-button {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%) !important;
  color: white !important;
  border-radius: 12px;
  padding: 0 24px;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
}

.close-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
}

/* Animations */
@keyframes gradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.05); }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
}

@keyframes ringPulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
}

/* Responsive Design */
@media (max-width: 960px) {
  .header-content {
    padding: 20px 24px;
  }
  
  .header-left {
    gap: 16px;
  }
  
  .header-title {
    font-size: 24px;
  }
  
  .leaderboard-container {
    padding: 0 24px 20px;
  }
  
  .search-section {
    padding: 20px 24px 16px;
  }
  
  .section-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .results-info {
    text-align: center;
  }
  
  .participant-item {
    padding: 16px 20px;
  }
  
  .participant-stats {
    min-width: 100px;
  }
  
  .footer-actions {
    padding: 20px 24px;
    flex-direction: column;
  }
  
  .pagination-container {
    order: 2;
  }
  
  .action-buttons {
    order: 1;
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .header-content {
    padding: 16px 20px;
  }
  
  .header-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .header-title {
    font-size: 20px;
  }
  
  .header-subtitle {
    font-size: 14px;
  }
  
  .leaderboard-container {
    padding: 0 20px 16px;
  }
  
  .search-section {
    padding: 16px 20px 12px;
  }
  
  .participant-item {
    padding: 12px 16px;
    flex-wrap: wrap;
  }
  
  .participant-rank {
    min-width: 60px;
    margin-right: 12px;
  }
  
  .rank-number {
    font-size: 16px;
  }
  
  .participant-avatar-container {
    margin-right: 16px;
  }
  
  .participant-info {
    flex: 1;
    min-width: 150px;
    margin-right: 16px;
  }
  
  .participant-details {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .participant-stats {
    min-width: auto;
    margin-right: 0;
    order: 4;
    flex-basis: 100%;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #f0f2f5;
  }
  
  .progress-section {
    flex: 1;
    margin-left: 16px;
  }
  
  .participant-actions {
    opacity: 1;
    margin-left: auto;
  }
  
  .footer-actions {
    padding: 16px 20px;
  }
  
  .action-buttons {
    gap: 12px;
  }
  
  .close-button {
    padding: 0 20px;
  }
}
</style>